import { catchError, tap } from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { MonoTypeOperatorFunction, Observable, OperatorFunction } from 'rxjs';

import { HttpErrorResponse } from '@angular/common/http';

import { captureException, ErrorContext } from '@app/utility/sentry.utils';

/**
 * Error categories for Sentry
 */
export enum SentryErrorCategory {
  API_ERROR = 'API_ERROR',
  NETWORK_ERROR = 'NETWORK_ERROR',
  JS_ERROR = 'JS_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  CRITICAL_BUSINESS_ERROR = 'CRITICAL_BUSINESS_ERROR',
}

/**
 * Interface for additional context passed to Sentry error capture functions
 * All properties are optional to allow flexible usage
 */
export interface AdditionalErrorContext {
  /** Component or service name where the error occurred */
  component?: string;
  /** HTTP request payload/body sent to the server */
  payload?: any;
  /** HTTP response body received from the server */
  response?: any;
  /** HTTP status text (e.g., "Bad Request", "Not Found") */
  statusText?: string;
  /** Detailed error information from the server response */
  errorDetails?: {
    error_type?: string;
    message?: string;
    [key: string]: any;
  };
  /** Error type identifier from API response */
  error_type?: string;
  /** User-related data for context */
  userData?: any;
  /** User ID for context */
  userId?: string | number;
  /** Any additional dynamic properties */
  [key: string]: any;
}

/**
 * Global registry for user and company context
 * This allows the standalone getUserCompanyContext function to access
 * user and company data without requiring dependency injection
 */
interface SentryContextRegistry {
  userId?: number;
  userName?: string;
  userEmail?: string;
  companyId?: number;
  companyName?: string;
}

// Global registry instance
let globalSentryContext: SentryContextRegistry = {};

/**
 * List of error messages to ignore (not send to Sentry)
 * Add error messages or patterns here that should not be reported
 */
const IGNORED_ERROR_MESSAGES: string[] = [
  'You do not own any company page',
  'You do not own any company pages',
  'No company page found',
  // Add more error messages to ignore here
];

/**
 * List of error message patterns (regex) to ignore
 * Use this for more flexible pattern matching
 */
const IGNORED_ERROR_PATTERNS: RegExp[] = [
  /you do not own any company page/i,
  /no company page/i,
  // Add more regex patterns here
];

/**
 * List of error types to ignore (based on error_type field)
 * These are typically from API responses with error_type field
 */
const IGNORED_ERROR_TYPES: string[] = [
  'InvalidCompanyNameException',
  'PendingScheduledSharesException',
  // Add more error types to ignore here
];

/**
 * Check if the URL is for a static asset (images, fonts, CSS, JS)
 * These errors are typically not actionable and add noise to Sentry
 * @param url - The URL to check
 * @returns true if the URL is for a static asset
 */
function isAssetUrl(url?: string): boolean {
  if (!url) return false;

  const lowerUrl = url.toLowerCase();
  return lowerUrl.includes('/assets/') ||
    lowerUrl.endsWith('.svg') ||
    lowerUrl.endsWith('.png') ||
    lowerUrl.endsWith('.jpg') ||
    lowerUrl.endsWith('.jpeg') ||
    lowerUrl.endsWith('.gif') ||
    lowerUrl.endsWith('.webp') ||
    lowerUrl.endsWith('.ico') ||
    lowerUrl.endsWith('.woff') ||
    lowerUrl.endsWith('.woff2') ||
    lowerUrl.endsWith('.ttf') ||
    lowerUrl.endsWith('.eot') ||
    lowerUrl.endsWith('.css') ||
    lowerUrl.endsWith('.js');
}

/**
 * Check if an error should be ignored based on its message, type, or URL
 * @param errorMessage - The error message to check
 * @param errorType - Optional error type from error response (e.g., error_type field)
 * @param url - Optional URL to check for static assets
 * @returns true if the error should be ignored, false otherwise
 */
function shouldIgnoreError(errorMessage: string, errorType?: string, url?: string): boolean {
  // Check if error is for a static asset URL
  if (isAssetUrl(url)) {
    return true;
  }

  if (!errorMessage && !errorType) return false;

  // Check error type first (if provided)
  if (errorType && IGNORED_ERROR_TYPES.some(type => type === errorType)) {
    return true;
  }

  // Check exact matches in error message
  if (errorMessage && IGNORED_ERROR_MESSAGES.some(msg => errorMessage.includes(msg))) {
    return true;
  }

  // Check regex patterns
  if (errorMessage && IGNORED_ERROR_PATTERNS.some(pattern => pattern.test(errorMessage))) {
    return true;
  }

  return false;
}

/**
 * Interface for user and company context
 */
export interface UserCompanyContext {
  userId?: number;
  userName?: string;
  userEmail?: string;
  companyId?: number;
  companyName?: string;
}

/**
 * Get the current page URL
 * @returns Current page URL or 'unknown' if not available
 */
function getCurrentPageUrl(): string {
  try {
    if (typeof window !== 'undefined' && window.location) {
      return window.location.href;
    }
    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Truncate stack_trace in response object to limit size
 * @param response - Response object that may contain stack_trace
 * @param maxItems - Maximum number of stack trace items to keep (default: 15)
 * @returns Response object with truncated stack_trace
 */
function truncateStackTrace(response: any, maxItems: number = 15): any {
  if (!response) return response;

  try {
    // Deep clone to avoid mutating the original
    const truncatedResponse = JSON.parse(JSON.stringify(response));

    // Check if there's a stack_trace in the exception object
    if (truncatedResponse?.exception?.stack_trace && Array.isArray(truncatedResponse.exception.stack_trace)) {
      const originalLength = truncatedResponse.exception.stack_trace.length;
      if (originalLength > maxItems) {
        truncatedResponse.exception.stack_trace = truncatedResponse.exception.stack_trace.slice(0, maxItems);
        truncatedResponse.exception.stack_trace_truncated = true;
        truncatedResponse.exception.stack_trace_original_length = originalLength;
      }
    }

    return truncatedResponse;
  } catch (error) {
    // If truncation fails, return original response
    return response;
  }
}

/**
 * Categorize status 0 errors based on error message patterns
 * Status 0 indicates a client-side network error where no HTTP response was received
 * @param errorMessage - The error message to analyze
 * @returns Specific category of the network error
 */
function categorizeStatus0Error(errorMessage?: string): string {
  if (!errorMessage) {
    // Check if browser is offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return 'Network Error (Offline)';
    }
    return 'Network Error (No Response)';
  }

  const message = errorMessage.toLowerCase();

  // Check for offline/connectivity issues
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return 'Network Error (Offline)';
  }
  if (message.includes('offline') || message.includes('no internet') || message.includes('network error')) {
    return 'Network Error (Offline/Connectivity)';
  }

  // Check for CORS violations
  if (message.includes('cors') || message.includes('cross-origin') || message.includes('access-control')) {
    return 'Network Error (CORS Blocked)';
  }

  // Check for aborted/cancelled requests
  if (message.includes('aborted') || message.includes('cancelled') || message.includes('canceled')) {
    return 'Network Error (Request Cancelled)';
  }

  // Check for timeout issues
  if (message.includes('timeout') || message.includes('timed out')) {
    return 'Network Error (Timeout)';
  }

  // Check for DNS resolution failures
  if (message.includes('dns') || message.includes('name resolution') || message.includes('nxdomain') || message.includes('getaddrinfo')) {
    return 'Network Error (DNS Failure)';
  }

  // Check for SSL/TLS certificate issues
  if (message.includes('ssl') || message.includes('tls') || message.includes('certificate')) {
    return 'Network Error (SSL/Certificate Error)';
  }

  // Check for connection refused
  if (message.includes('refused') || message.includes('econnrefused') || message.includes('connection refused')) {
    return 'Network Error (Connection Refused)';
  }

  // Check for mixed content (HTTP/HTTPS mismatch)
  if (message.includes('mixed content') || message.includes('insecure') || message.includes('blocked:mixed')) {
    return 'Network Error (Mixed Content Blocked)';
  }

  // Check for server unreachable
  if (message.includes('unreachable') || message.includes('econnreset') || message.includes('connection reset')) {
    return 'Network Error (Server Unreachable)';
  }

  return 'Network Error (No Response)';
}

/**
 * Interface for malformed URL detection result
 */
interface MalformedUrlInfo {
  /** Whether the URL contains malformed values */
  isMalformed: boolean;
  /** List of issues found in the URL */
  issues: string[];
  /** Malformed values found (NaN, undefined, null, etc.) */
  malformedValues: string[];
}

/**
 * Detect malformed values in API URLs (NaN, undefined, null, [object Object])
 * These indicate bugs where values weren't validated before making API calls
 * @param url - The API URL to check
 * @returns MalformedUrlInfo with details about any issues found
 */
function detectMalformedUrl(url: string): MalformedUrlInfo {
  const issues: string[] = [];
  const malformedValues: string[] = [];

  // Patterns to detect in URLs
  const malformedPatterns = [
    { pattern: /\/NaN(\/|$|\?)/g, value: 'NaN', issue: 'NaN in URL path (invalid numeric ID)' },
    { pattern: /\/undefined(\/|$|\?)/g, value: 'undefined', issue: 'undefined in URL path' },
    { pattern: /\/null(\/|$|\?)/g, value: 'null', issue: 'null in URL path' },
    { pattern: /\/\[object\s+Object\](\/|$|\?)/gi, value: '[object Object]', issue: '[object Object] in URL path' },
    { pattern: /[?&][^=]+=NaN(&|$)/g, value: 'NaN', issue: 'NaN in query parameter' },
    { pattern: /[?&][^=]+=undefined(&|$)/g, value: 'undefined', issue: 'undefined in query parameter' },
    { pattern: /[?&][^=]+=null(&|$)/g, value: 'null', issue: 'null in query parameter' },
    { pattern: /[?&][^=]+=\[object\s+Object\](&|$)/gi, value: '[object Object]', issue: '[object Object] in query parameter' },
  ];

  for (const { pattern, value, issue } of malformedPatterns) {
    if (pattern.test(url)) {
      issues.push(issue);
      if (!malformedValues.includes(value)) {
        malformedValues.push(value);
      }
    }
  }

  return {
    isMalformed: issues.length > 0,
    issues,
    malformedValues,
  };
}

/**
 * Get human-readable status text for HTTP status codes
 * @param status - HTTP status code
 * @param errorMessage - Optional error message for detailed status 0 categorization
 * @returns Human-readable status text
 */
function getStatusText(status: number, errorMessage?: string): string {
  // Special handling for status 0 (client-side network errors)
  if (status === 0) {
    return categorizeStatus0Error(errorMessage);
  }

  const statusTexts: Record<number, string> = {
    200: 'OK',
    201: 'Created',
    204: 'No Content',
    400: 'Bad Request',
    401: 'Unauthorized',
    403: 'Forbidden',
    404: 'Not Found',
    405: 'Method Not Allowed',
    408: 'Request Timeout',
    409: 'Conflict',
    422: 'Unprocessable Entity',
    429: 'Too Many Requests',
    500: 'Internal Server Error',
    502: 'Bad Gateway',
    503: 'Service Unavailable',
    504: 'Gateway Timeout',
  };
  return statusTexts[status] || 'Unknown Status';
}

/**
 * Set user and company context in the global registry
 * This should be called when user profile is fetched or updated
 * @param context - User and company context to store
 */
export function setSentryContext(context: SentryContextRegistry): void {
  try {
    globalSentryContext = {
      ...globalSentryContext,
      ...context,
    };
  } catch (error) {
    console.warn('Failed to set Sentry context:', error);
  }
}

/**
 * Clear user and company context from the global registry
 * This should be called on logout
 */
export function clearSentryContext(): void {
  globalSentryContext = {};
}

/**
 * Get user and company context from multiple sources:
 * 1. Global registry (set by AuthService when user profile is fetched)
 * 2. JWT token (contains userId and companyId)
 * 3. localStorage (fallback for userId)
 *
 * This function extracts user and company information without requiring dependency injection
 * @returns UserCompanyContext object with user and company information
 */
function getUserCompanyContext(): UserCompanyContext {
  try {
    const context: UserCompanyContext = {};
    const helper = new JwtHelperService();

    // First, try to get data from global registry (most complete source)
    if (globalSentryContext.userId) {
      context.userId = globalSentryContext.userId;
    }
    if (globalSentryContext.userName) {
      context.userName = globalSentryContext.userName;
    }
    if (globalSentryContext.userEmail) {
      context.userEmail = globalSentryContext.userEmail;
    }
    if (globalSentryContext.companyId) {
      context.companyId = globalSentryContext.companyId;
    }
    if (globalSentryContext.companyName) {
      context.companyName = globalSentryContext.companyName;
    }

    // Second, try to get data from JWT token (fallback)
    const token = localStorage.getItem('token') || localStorage.getItem('tokenUser');

    if (token && !helper.isTokenExpired(token)) {
      const decodedToken = helper.decodeToken(token);

      // Extract user information from token
      if (decodedToken) {
        // User ID (from JWT 'sub' field)
        if (!context.userId && (decodedToken.user_id || decodedToken.id || decodedToken.sub)) {
          const userId = decodedToken.user_id || decodedToken.id || decodedToken.sub;
          // Convert string to number if needed
          context.userId = typeof userId === 'string' ? parseInt(userId, 10) : userId;
        }

        // User email (rarely in JWT, but check anyway)
        if (!context.userEmail && decodedToken.email) {
          context.userEmail = decodedToken.email;
        }

        // User name (rarely in JWT, but check anyway)
        if (!context.userName) {
          if (decodedToken.first_name || decodedToken.last_name) {
            context.userName = `${decodedToken.first_name || ''} ${decodedToken.last_name || ''}`.trim();
          } else if (decodedToken.name) {
            context.userName = decodedToken.name;
          }
        }

        // Company ID (from JWT 'client_id' field)
        if (!context.companyId && (decodedToken.client_id || decodedToken.company_id)) {
          context.companyId = decodedToken.client_id || decodedToken.company_id;
        }

        // Company name (rarely in JWT, but check anyway)
        if (!context.companyName && (decodedToken.client_name || decodedToken.company_name)) {
          context.companyName = decodedToken.client_name || decodedToken.company_name;
        }
      }
    }

    // Third, try to get userId from localStorage as final fallback
    if (!context.userId) {
      const userIdFromStorage = localStorage.getItem('userId');
      if (userIdFromStorage) {
        context.userId = parseInt(userIdFromStorage, 10);
      }
    }

    return context;
  } catch (error) {
    // If there's an error getting context, return empty object
    // We don't want to fail error reporting because we couldn't get context
    console.warn('Failed to get user/company context for Sentry:', error);
    return {};
  }
}

/**
 * Extract endpoint name from URL for error grouping
 * @param url - Full API URL
 * @returns Clean endpoint name
 */
export function extractEndpointFromUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    let pathname = urlObj.pathname;

    // Remove /api prefix if present
    pathname = pathname.replace(/^\/api\//, '');

    // Split by / and filter out numeric IDs
    const parts = pathname.split('/').filter(p => p && !p.match(/^\d+$/));

    // Get the last 2-3 parts for better context
    const endpoint = parts.slice(-3).join('/') || 'unknown';

    return endpoint;
  } catch {
    return 'unknown';
  }
}

/**
 * Extract file name from error stack trace
 * @param error - Error object
 * @returns File name or 'unknown'
 */
export function extractFileNameFromError(error: Error): string {
  try {
    if (!error.stack) return 'unknown';

    // Parse stack trace to get the first file reference
    const stackLines = error.stack.split('\n');
    
    // Look for the first line with a file reference (usually line 2)
    for (const line of stackLines) {
      // Match patterns like: at functionName (file.ts:line:col) or at file.ts:line:col
      const match = line.match(/(?:at\s+.*?\s+\()?([^()\s]+\.(?:ts|js|component\.ts|service\.ts)):(\d+):(\d+)/);
      if (match) {
        const fullPath = match[1];
        // Extract just the file name
        const fileName = fullPath.split('/').pop() || fullPath.split('\\').pop() || fullPath;
        return fileName;
      }
    }

    return 'unknown';
  } catch {
    return 'unknown';
  }
}

/**
 * Capture API error with proper categorization and naming
 * Automatically includes user and company context
 * Automatically filters out ignored errors
 * @param method - HTTP method (GET, POST, etc.)
 * @param url - API URL
 * @param status - HTTP status code
 * @param errorMessage - Error message
 * @param additionalContext - Additional context to include (can include error_type)
 */
export function captureApiError(
  method: string,
  url: string,
  status: number,
  errorMessage: string,
  additionalContext?: AdditionalErrorContext
): void {
  // Don't log authentication errors (401) to Sentry
  if (status === 401) {
    console.log(`Ignoring ${status} error (not sent to Sentry)`);
    return;
  }

  // Extract error_type from additionalContext if available
  const errorType = additionalContext?.errorDetails?.error_type || additionalContext?.error_type;

  // Check if this error should be ignored (including static asset URLs)
  if (shouldIgnoreError(errorMessage, errorType, url)) {
    console.log('Ignoring API error (filtered):', { errorMessage, errorType, url });
    return;
  }

  const endpoint = extractEndpointFromUrl(url);

  // Detect malformed URL values (NaN, undefined, null, [object Object])
  const malformedUrlInfo = detectMalformedUrl(url);

  // Add indicator to title if URL is malformed
  const malformedIndicator = malformedUrlInfo.isMalformed ? ' [MALFORMED_URL]' : '';
  const errorTitle = `[${SentryErrorCategory.API_ERROR}] ${method} /${endpoint} - ${status}${malformedIndicator}`;

  // Get user and company context
  const userCompanyContext = getUserCompanyContext();

  // Get current page URL
  const pageUrl = getCurrentPageUrl();

  // Truncate stack_trace in response to limit size (max 15 items)
  const rawResponse = additionalContext?.response || additionalContext?.errorDetails;
  const truncatedResponse = truncateStackTrace(rawResponse, 15);

  const errorContext: ErrorContext = {
    title: errorTitle,
    message: errorMessage,
    apiUrl: url, // API endpoint URL
    method,
    status, // HTTP status code
    statusText: getStatusText(status, errorMessage),
    type: SentryErrorCategory.API_ERROR,
    category: SentryErrorCategory.API_ERROR,
    endpoint,
    pageUrl, // Include current page URL
    errorType, // Include error_type in context
    // Malformed URL detection
    hasMalformedUrl: malformedUrlInfo.isMalformed,
    malformedUrlIssues: malformedUrlInfo.isMalformed ? malformedUrlInfo.issues : undefined,
    malformedValues: malformedUrlInfo.isMalformed ? malformedUrlInfo.malformedValues : undefined,
    // User and company context
    ...userCompanyContext,
    // Request payload sent to the server
    payload: additionalContext?.payload,
    // Response received from the server (with truncated stack_trace)
    response: truncatedResponse,
    // Error details
    errorDetails: additionalContext?.errorDetails,
    // Spread any additional context properties
    ...additionalContext,
  };

  captureException(errorContext);
}

/**
 * Capture network error with proper categorization and naming
 * Automatically includes user and company context
 * Automatically filters out ignored errors
 * @param method - HTTP method
 * @param url - API URL
 * @param errorMessage - Error message
 * @param additionalContext - Additional context
 */
export function captureNetworkError(
  method: string,
  url: string,
  errorMessage: string,
  additionalContext?: AdditionalErrorContext
): void {
  // Check if this error should be ignored (including static asset URLs)
  if (shouldIgnoreError(errorMessage, undefined, url)) {
    console.log('Ignoring network error (filtered):', { errorMessage, url });
    return;
  }

  const endpoint = extractEndpointFromUrl(url);

  // Detect malformed URL values (NaN, undefined, null, [object Object])
  const malformedUrlInfo = detectMalformedUrl(url);

  // Add indicator to title if URL is malformed
  const malformedIndicator = malformedUrlInfo.isMalformed ? ' [MALFORMED_URL]' : '';
  const errorTitle = `[${SentryErrorCategory.NETWORK_ERROR}] ${method} /${endpoint}${malformedIndicator}`;

  // Get user and company context
  const userCompanyContext = getUserCompanyContext();

  // Get current page URL
  const pageUrl = getCurrentPageUrl();

  // Truncate stack_trace in response to limit size (max 15 items)
  const rawResponse = additionalContext?.response || additionalContext?.errorDetails;
  const truncatedResponse = truncateStackTrace(rawResponse, 15);

  const errorContext: ErrorContext = {
    title: errorTitle,
    message: errorMessage,
    apiUrl: url, // API endpoint URL
    method,
    // For network errors (status 0), use detailed categorization
    statusText: getStatusText(0, errorMessage),
    type: SentryErrorCategory.NETWORK_ERROR,
    category: SentryErrorCategory.NETWORK_ERROR,
    endpoint,
    pageUrl, // Include current page URL
    // Malformed URL detection
    hasMalformedUrl: malformedUrlInfo.isMalformed,
    malformedUrlIssues: malformedUrlInfo.isMalformed ? malformedUrlInfo.issues : undefined,
    malformedValues: malformedUrlInfo.isMalformed ? malformedUrlInfo.malformedValues : undefined,
    // User and company context
    ...userCompanyContext,
    // Request payload sent to the server
    payload: additionalContext?.payload,
    // Response received (usually null for network errors, with truncated stack_trace)
    response: truncatedResponse,
    // Error details
    errorDetails: additionalContext?.errorDetails,
    // Spread any additional context properties
    ...additionalContext,
  };

  captureException(errorContext);
}

/**
 * Capture JavaScript error with proper categorization and file name
 * Automatically includes user and company context
 * Automatically filters out ignored errors
 * @param error - Error object
 * @param context - Additional context (component name, method name, etc.)
 * @param additionalContext - Additional context to include
 */
export function captureJsError(
  error: Error,
  context: string,
  additionalContext?: AdditionalErrorContext
): void {
  // Check if this error should be ignored
  if (shouldIgnoreError(error.message)) {
    console.log('Ignoring JS error (filtered):', error.message);
    return;
  }

  const fileName = extractFileNameFromError(error);
  const errorTitle = `[${SentryErrorCategory.JS_ERROR}] ${fileName} - ${context}`;

  // Get user and company context
  const userCompanyContext = getUserCompanyContext();

  // Get current page URL
  const pageUrl = getCurrentPageUrl();

  // From additionalContext, keep everything except payload/response.
  // We still want errorDetails and any other custom fields.
  const { payload: _ignoredPayload, response: _ignoredResponse, errorDetails, ...restAdditionalContext } =
    additionalContext || {};

  const errorContext: ErrorContext = {
    title: errorTitle,
    message: error.message,
    type: SentryErrorCategory.JS_ERROR,
    category: SentryErrorCategory.JS_ERROR,
    fileName,
    context,
    pageUrl, // Include current page URL
    stack: error.stack,
    // User and company context
    ...userCompanyContext,
    // Explicitly include error details
    errorDetails,
    // Spread any remaining additional context (excluding payload/response)
    ...restAdditionalContext,
  };

  captureException(errorContext);
}

/**
 * Capture critical business logic errors with strong grouping and context.
 * Use this for violated domain invariants and malformed business inputs.
 * @param errorOrMessage - Error object or message string
 * @param context - Context string (e.g., 'ServiceName.methodName')
 * @param additionalContext - Additional context to include
 */
export function captureCriticalBusinessError(
  errorOrMessage: Error | string,
  context: string,
  additionalContext?: AdditionalErrorContext
): void {
  const error = errorOrMessage instanceof Error ? errorOrMessage : new Error(errorOrMessage);
  const fileName = extractFileNameFromError(error);
  const errorTitle = `[${SentryErrorCategory.CRITICAL_BUSINESS_ERROR}] ${context}`;

  const userCompanyContext = getUserCompanyContext();
  const pageUrl = getCurrentPageUrl();

  const { payload: _ignoredPayload, response: _ignoredResponse, errorDetails, ...restAdditionalContext } =
    additionalContext || {};

  const errorContext: ErrorContext = {
    title: errorTitle,
    message: error.message,
    type: SentryErrorCategory.CRITICAL_BUSINESS_ERROR,
    category: SentryErrorCategory.CRITICAL_BUSINESS_ERROR,
    fileName,
    context,
    pageUrl,
    stack: error.stack,
    ...userCompanyContext,
    errorDetails,
    ...restAdditionalContext,
  };

  captureException(errorContext);
}

/**
 * RxJS tap operator to capture API errors
 * Use this in the tap operator to automatically log API errors to Sentry
 *
 * @example
 * ```typescript
 * this.http.get('/api/users').pipe(
 *   tapApiError('GET', '/api/users'),
 *   map(response => response.data)
 * )
 * ```
 *
 * @param method - HTTP method
 * @param url - API URL
 * @param additionalContext - Additional context to include
 * @returns RxJS MonoTypeOperatorFunction
 */
export function tapApiError<T>(
  method: string,
  url: string,
  additionalContext?: AdditionalErrorContext
): MonoTypeOperatorFunction<T> {
  return tap({
    error: (error: any) => {
      const status = error?.status || 0;
      const errorMessage = error?.message || error?.error?.message || 'Unknown API error';

      captureApiError(method, url, status, errorMessage, {
        ...additionalContext,
        errorDetails: error?.error,
      });
    }
  });
}

/**
 * RxJS catchError operator to capture JavaScript errors
 * Use this in the catchError operator to automatically log JS errors to Sentry
 *
 * @example
 * ```typescript
 * this.someObservable$.pipe(
 *   map(data => this.processData(data)),
 *   catchJsError('MyComponent.processData', { userId: this.userId })
 * )
 * ```
 *
 * @param context - Context string (e.g., 'ComponentName.methodName')
 * @param additionalContext - Additional context to include
 * @param fallbackValue - Optional fallback value to return on error
 * @returns RxJS OperatorFunction
 */
export function catchJsError<T, R = T>(
  context: string,
  additionalContext?: AdditionalErrorContext,
  fallbackValue?: R
): OperatorFunction<T, T | R> {
  return catchError((error: any) => {
    // Convert to Error if it's not already
    const errorObj = error instanceof Error ? error : new Error(String(error));

    captureJsError(errorObj, context, additionalContext);

    // Re-throw the error or return fallback value
    if (fallbackValue !== undefined) {
      return new Observable<R>(subscriber => {
        subscriber.next(fallbackValue);
        subscriber.complete();
      });
    }

    throw error;
  });
}

/**
 * RxJS catchError operator with custom error handler and Sentry logging
 * Use this when you need both Sentry logging and custom error handling
 *
 * @example
 * ```typescript
 * this.http.get('/api/users').pipe(
 *   catchJsErrorWithHandler(
 *     'UserService.getUsers',
 *     (error) => {
 *       this.notificationService.showError('Failed to load users');
 *       return of([]);
 *     },
 *     { component: 'UserListComponent' }
 *   )
 * )
 * ```
 *
 * @param context - Context string
 * @param errorHandler - Custom error handler function
 * @param additionalContext - Additional context to include
 * @returns RxJS OperatorFunction
 */
export function catchJsErrorWithHandler<T, R>(
  context: string,
  errorHandler: (error: any) => Observable<R>,
  additionalContext?: AdditionalErrorContext
): OperatorFunction<T, T | R> {
  return catchError((error: any) => {
    // Convert to Error if it's not already
    const errorObj = error instanceof Error ? error : new Error(String(error));

    captureJsError(errorObj, context, additionalContext);

    // Call custom error handler
    return errorHandler(error);
  });
}

/**
 * RxJS operator to handle errors with loading state reset and fallback value.
 * Keeps UI responsive while preserving JS error reporting to Sentry.
 *
 * @param resetCallback - Callback to reset loading state
 * @param fallbackValue - Value to return on error
 * @param context - Optional context for logging
 * @returns RxJS operator
 */
export function handleErrorWithLoading<T, R = T>(
  resetCallback: () => void,
  fallbackValue: R | (() => R),
  context?: string
): (source: Observable<T>) => Observable<T | R> {
  return catchError((error: any) => {
    resetCallback();

    if (context && !(error instanceof HttpErrorResponse)) {
      const errorObj = error instanceof Error ? error : new Error(String(error));
      captureJsError(errorObj, context);
    }

    const value = typeof fallbackValue === 'function' ? (fallbackValue as () => R)() : fallbackValue;
    return new Observable<T | R>((subscriber) => {
      subscriber.next(value);
      subscriber.complete();
    });
  });
}
